import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';

import useProjectOrGlobalCostcodes from './useProjectOrGlobalCostcodes';

/**
 * Union of the columns every global cost code consumer under the project drawer needs.
 * Requesting the union once is what lets a single fetch serve all of them - the
 * selection tables need the wage/billing fields, the template pickers only need ids.
 */
const GLOBAL_COSTCODE_COLUMNS = [
  'description',
  'hourlyWage',
  'dailyWage',
  'wageAdjustment',
  'wageMultiplier',
  'hourlyBillingRate',
  'dailyBillingRate',
];

const ProjectGlobalCostcodesContext = createContext({
  globalCostcodes: [],
  loading: false,
  ensureLoaded: () => {},
});

/**
 * Holds one division's global cost codes for everything rendered inside a project.
 * Fetching stays lazy: nothing is requested until a descendant that actually needs
 * them asks, so opening a project costs nothing on its own.
 */
export function ProjectGlobalCostcodesProvider({ divisionId, children }) {
  const [requested, setRequested] = useState(false);

  const ensureLoaded = useCallback(() => setRequested(true), []);

  // Deliberately not reset when divisionId changes: child effects run before parent
  // effects, so clearing it here would cancel a consumer's request instead of
  // reissuing it. useProjectOrGlobalCostcodes already refetches on a division change.

  const { costcodes, loading } = useProjectOrGlobalCostcodes({
    projectId: null,
    divisionId,
    visible: requested,
    includeCategoryIds: true,
    columns: GLOBAL_COSTCODE_COLUMNS,
  });

  const value = useMemo(() => ({
    globalCostcodes: costcodes,
    loading,
    ensureLoaded,
  }), [costcodes, loading, ensureLoaded]);

  return (
    <ProjectGlobalCostcodesContext.Provider value={value}>
      {children}
    </ProjectGlobalCostcodesContext.Provider>
  );
}

ProjectGlobalCostcodesProvider.propTypes = {
  divisionId: PropTypes.string,
  children: PropTypes.node,
};

ProjectGlobalCostcodesProvider.defaultProps = {
  divisionId: undefined,
  children: null,
};

/**
 * @param {boolean} shouldLoad Pass the consumer's own visibility so the shared fetch
 * is only triggered when something is actually about to show cost codes.
 */
export default function useProjectGlobalCostcodes(shouldLoad = true) {
  const { globalCostcodes, loading, ensureLoaded } = useContext(ProjectGlobalCostcodesContext);

  useEffect(() => {
    if (shouldLoad) ensureLoaded();
  }, [shouldLoad, ensureLoaded]);

  return { globalCostcodes, loading };
}
